import { useTranslation } from 'react-i18next';

import { useElectronUpdateStatus } from 'src/features/electron/ElectronUpdateContext';
import { getSafeElectronAPI, isElectron } from 'src/hooks/useElectron';
import { useTrackAppVersion } from 'src/hooks/useTrackAppVersion';

export type SidebarUpdate = {
  title: string;
  description: string;
  apply: () => void;
};

/**
 * The pending app update, if any: a downloaded desktop build or a newer web
 * deploy. Shared by the standalone update card and the notification stack so
 * both agree on when an update exists.
 */
export function useSidebarUpdate(): SidebarUpdate | null {
  const { t } = useTranslation();
  const { status } = useElectronUpdateStatus();
  const inElectron = isElectron();
  const { hasNewVersion } = useTrackAppVersion();

  const isElectronUpdate = inElectron && status === 'ready';
  const isWebUpdate = !inElectron && hasNewVersion;

  if (!isElectronUpdate && !isWebUpdate) {
    return null;
  }

  return {
    title: t(($) => $.desktop_app.update_toast.title),
    description: isWebUpdate
      ? t(($) => $.desktop_app.update_toast.web_description)
      : t(($) => $.desktop_app.update_toast.description),
    apply: () => {
      if (isElectronUpdate) {
        getSafeElectronAPI().installUpdate();
      } else {
        window.location.reload();
      }
    },
  };
}
